"use client"

import { cn } from "@/modules/helpers/hooks/shared/utils"

type LoadingPageProps = {
  background?: "white" | "blur" | "gradient"
  size?: "sm" | "md" | "lg"
  text?: string
}

export function LoadingPage({
  background = "white",
  size = "md",
  text = "Loading..."
}: LoadingPageProps) {
  const textSizeClasses = {
    sm: "text-sm",
    md: "text-lg",
    lg: "text-xl"
  }

  const renderAnimatedText = () => {
    if (!text) return null

    const characters = text.split("")

    return (
      <p className={cn("font-medium text-primary", textSizeClasses[size])}>
        {characters.map((char, index) => (
          <span
            key={index}
            className="inline-block animate-scale-char"
            style={{
              animationDelay: `${index * 0.1}s`,
              animationDuration: "1.5s",
              animationIterationCount: "infinite",
              animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
              minWidth: char === " " ? "0.3em" : "auto"
            }}
          >
            {char === " " ? "\u00A0" : char}
          </span>
        ))}
      </p>
    )
  }

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex flex-col items-center justify-center",
        background === "white" && "bg-white",
        background === "blur" && "bg-black/10 backdrop-blur-sm",
        background === "gradient" && "bg-gradient"
      )}
    >
      {/* Custom Double Ring Spinner */}
      <div className="flex-col gap-4 w-full flex items-center justify-center">
        <div
          className={cn(
            "border-4 border-transparent text-primary animate-spin flex items-center justify-center border-t-primary rounded-full",
            size === "sm"
              ? "w-12 h-12 text-2xl"
              : size === "md"
                ? "w-20 h-20 text-4xl"
                : "w-24 h-24 text-5xl"
          )}
        >
          <div
            className={cn(
              "border-4 border-transparent text-primary-green animate-spin flex items-center justify-center border-t-primary-green rounded-full",
              size === "sm"
                ? "w-8 h-8 text-lg"
                : size === "md"
                  ? "w-16 h-16 text-2xl"
                  : "w-20 h-20 text-3xl"
            )}
          ></div>
        </div>
      </div>

      {/* Loading Text */}
      {text && <div className="mt-4 text-center">{renderAnimatedText()}</div>}

      <span className="sr-only">Loading...</span>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes scale-char {
          0%,
          100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.15);
          }
        }

        .animate-scale-char {
          animation-name: scale-char;
        }
        `
        }}
      />
    </div>
  )
}
